import { message } from "antd";

import { isWebview } from "helpers/webview/utilities";

import { type IServiceCloudSettings } from "commonComponents/utilities/ClientChat/ServiceCloudTypes";

// eslint-disable-next-line no-restricted-syntax
const win = window as typeof window & {
    embeddedservice_bootstrap: {
        settings: { language: string };
        init: (
            orgId: string,
            channelName: string,
            endpointUrl: string,
            options: { scrt2URL: string },
        ) => void;
        prechatAPI: {
            setHiddenPrechatFields: (fields: Record<string, string>) => void;
        };
        utilAPI: {
            launchChat: () => Promise<string>;
            removeAllComponents: () => void;
            getEmbeddedMessagingFrame: () => HTMLIFrameElement | null;
        };
        userVerificationAPI: {
            clearSession: (clearAll: boolean) => Promise<void>;
        };
    };
};

export function initializeServiceCloudChat(settings: IServiceCloudSettings) {
    // Remove with SPA, service cloud should never render in an iframe
    if (win.top !== win.self) {
        return;
    }

    // Remove for webview contexts so chat doesn't load in mobile apps
    if (isWebview()) {
        return;
    }

    const existingScriptElement = document.getElementById(
        "bt-service-cloud-embeddedservice-bootstrap",
    );
    if (!existingScriptElement) {
        const s = document.createElement("script");
        s.id = "bt-service-cloud-embeddedservice-bootstrap";
        s.type = "text/javascript";
        s.async = true;
        s.src = `${settings.serviceCloudConfig.channelEndpointUrl}/assets/js/bootstrap.min.js`;
        const x = document.getElementsByTagName("script")[0];
        x.parentNode!.insertBefore(s, x);
        s.onload = function () {
            try {
                const embeddedServiceBootstrap = win.embeddedservice_bootstrap;
                if (!embeddedServiceBootstrap) {
                    throw new Error("Embedded service bootstrap is not available.");
                }

                embeddedServiceBootstrap.settings.language = "en_US";
                embeddedServiceBootstrap.init(
                    settings.serviceCloudConfig.salesforceOrgId,
                    settings.serviceCloudConfig.channelName,
                    settings.serviceCloudConfig.channelEndpointUrl,
                    {
                        scrt2URL: settings.serviceCloudConfig.configurationFileUrl,
                    },
                );
            } catch (err) {
                void message.error("Error loading embedded messaging");
            }
        };

        if (settings && settings.salesforceAccountId !== "") {
            win.addEventListener("onEmbeddedMessagingReady", async () => {
                const embeddedServiceBootstrap = win.embeddedservice_bootstrap;

                embeddedServiceBootstrap.prechatAPI.setHiddenPrechatFields({
                    SalesforceAccountID: settings.salesforceAccountId,
                    LoginID: settings.loginId.toString(),
                    GlobalUserID: settings.globalUserId.toString(),
                    Persona: settings.persona,
                    CompanyName: settings.companyName,
                    PrimaryContact: settings.primaryContact,
                    DisplayName: settings.displayName,
                    Email: settings.email,
                    CellPhone: settings.cellPhone,
                });
            });
        }

        win.addEventListener("onEmbeddedMessagingButtonCreated", () => {
            // Hide the chat bubble by default. It only becomes visible when the user
            // explicitly triggers the chat (e.g., from the Contact Us "Support Chat" button).
            // The close X is added in showChat() so it only appears alongside a visible bubble.
            hideChatButton();
        });

        win.addEventListener("onEmbeddedMessagingWindowMaximized", () => {
            const closeButton = document.getElementById("btnCloseServiceCloud");
            if (closeButton) {
                closeButton.remove();
            }
        });

        win.addEventListener("onEmbeddedMessagingWindowMinimized", () => {
            const closeButton = document.getElementById("btnCloseServiceCloud");
            // the close button may have already been added to the chat button rather than the iframe
            // if that is the case, we need to remove it before adding it back
            if (closeButton) {
                closeButton.remove();
            }
            addCloseElement();
        });
    }
}

// The close button lives in our DOM (not Salesforce's iframe) so keyboard users
// get a Tab-reachable, Enter/Esc-operable affordance — the iframe swallows focus
// and key events, so we can't reliably hover-reveal or Esc-dismiss via the bubble
// itself. It's only rendered while the bubble is visible.
function handleEscKey(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key === "Escape") {
        removeCloseButton(keyboardEvent);
    }
}

// Look up the bubble fresh each time — on the first showChat() Salesforce may
// have only rendered the conversation button; the iframe arrives later and
// replaces it as the visible bubble. A cached reference goes stale.
function removeCloseButton(e: Event) {
    const bubble = getServiceCloudBubbleElement();
    if (bubble) {
        bubble.style.display = "none";
    }
    document.getElementById("btnCloseServiceCloud")?.remove();
    e.preventDefault();
}

function addCloseElement() {
    const existingButton = document.getElementById("btnCloseServiceCloud");
    if (existingButton) return;

    const targetElement = getServiceCloudBubbleElement();

    if (!targetElement) {
        void message.warn("Could not find Service Cloud chat iframe or button");
        return;
    }

    const targetElementStyle = win.getComputedStyle(targetElement);
    if (!targetElementStyle) return;

    const closeButton = document.createElement("button");
    closeButton.id = "btnCloseServiceCloud";
    closeButton.type = "button";
    closeButton.setAttribute("aria-label", "Close chat");
    closeButton.title = "Close chat";
    // Chrome's responsive design mode triggers Salesforce's mobile path, which positions
    // the iframe via inset/transform rather than right/bottom — computed values come back
    // as "auto" or 0 and our subtraction pushes the X off-screen. Fall back to offsets
    // matching where the bubble lives in our CSS (.embeddedMessagingConversationButton).
    const rightValue = Number.parseFloat(targetElementStyle.right);
    const bottomValue = Number.parseFloat(targetElementStyle.bottom);
    closeButton.style.right = `${
        Number.isFinite(rightValue) && rightValue > 0 ? rightValue - 10 : 10
    }px`;
    closeButton.style.bottom = `${
        Number.isFinite(bottomValue) && bottomValue > 0 ? bottomValue - 5 : 50
    }px`;

    closeButton.addEventListener("click", removeCloseButton);
    closeButton.addEventListener("keydown", handleEscKey);

    targetElement.parentElement!.appendChild(closeButton);
}

function getServiceCloudBubbleElement(): HTMLElement | null {
    // first try to get the embedded messaging frame if it's loaded;
    // if it's not present, fall back to the button element available before the iframe loads
    return (
        win.embeddedservice_bootstrap?.utilAPI?.getEmbeddedMessagingFrame() ??
        document.getElementById("embeddedMessagingConversationButton")
    );
}

// Salesforce's onEmbeddedMessagingWindowMinimized doesn't fire on touch/
// responsive viewports, so we watch the iframe's size directly: the bubble
// (~50px) and the open chat field (much larger) are clearly distinguishable,
// and the transition open → minimized is when we want the X to (re)appear.
let chatStateObserver: ResizeObserver | null = null;
let previousChatState: "open" | "minimized" | "hidden" = "hidden";

function ensureChatStateObserver(retriesLeft = 5) {
    if (chatStateObserver) return;

    const iframe = win.embeddedservice_bootstrap?.utilAPI?.getEmbeddedMessagingFrame();
    if (!iframe) {
        // Iframe is created during launchChat; retry briefly until it exists.
        if (retriesLeft > 0) {
            win.setTimeout(() => ensureChatStateObserver(retriesLeft - 1), 500);
        }
        return;
    }

    chatStateObserver = new ResizeObserver(() => {
        const rect = iframe.getBoundingClientRect();
        let newState: typeof previousChatState;
        if (rect.width === 0 || rect.height === 0) {
            newState = "hidden";
        } else if (rect.width < 100 && rect.height < 100) {
            newState = "minimized";
        } else {
            newState = "open";
        }

        if (newState === "minimized" && previousChatState === "open") {
            addCloseElement();
        } else if (newState === "open") {
            document.getElementById("btnCloseServiceCloud")?.remove();
        }

        previousChatState = newState;
    });

    chatStateObserver.observe(iframe);
}

function hideChatButton() {
    const bubble = getServiceCloudBubbleElement();
    if (bubble) {
        bubble.style.display = "none";
    }
}

export function showChat() {
    const embeddedServiceBootstrap = win.embeddedservice_bootstrap;
    // The chat bubble is hidden on load; reveal it now that the user has triggered the chat.
    // The chat can take a while to launch if it's the first time it is opened,
    // so show the button immediately so the loading indicator can be visible while the chat loads.
    // The close X is intentionally not added here: launchChat() opens the chat field directly,
    // and onEmbeddedMessagingWindowMaximized doesn't fire for that initial open, so adding the X
    // would leak it on top of the field. The X is added by onEmbeddedMessagingWindowMinimized
    // when the user later minimizes the field back to the bubble.
    const bubble = getServiceCloudBubbleElement();
    if (bubble) {
        bubble.style.display = "";
    }
    embeddedServiceBootstrap.utilAPI.launchChat().catch((error) => {
        void message.error("Error launching chat. Error message: " + error);
    });
    ensureChatStateObserver();
}

export function shutdownChat() {
    const embeddedServiceBootstrap = win.embeddedservice_bootstrap;
    if (embeddedServiceBootstrap?.userVerificationAPI) {
        embeddedServiceBootstrap.userVerificationAPI
            .clearSession(false)
            .then(() => {
                // why do we have to do this? I have no idea but it works.
                void embeddedServiceBootstrap?.userVerificationAPI?.clearSession(true);
                embeddedServiceBootstrap?.utilAPI?.removeAllComponents();
            })
            .catch((error) => {
                void message.error(
                    "Error clearing session in shutdownChat: " + (error?.message || error),
                );
            });
    }
}
